'use client'

// Súper online PR 2b (Tarea 2b.2): selector de peso (− / valor / +) o de variante
// (radios) del producto. Lo monta ItemModal en lugar del selector de cantidad y de
// los modificadores, solo para lo que llegó con `online_retail_v2`
// (lib/microsite/retailLine.ts → needsRetailPicker). Controlado: el modal guarda el
// peso y la variante y arma la línea con `buildRetailCartLine`.
//
// El total que muestra sale de `micrositeLineSubtotal` sobre la MISMA línea que se
// agregaría: el número del selector, el del carrito y el del servidor son una cuenta.

import { useTranslations } from 'next-intl'
import { effectiveItemCardPrice, effectiveUnitPrice, type PricingConfig } from '@/lib/utils/pricing'
import { micrositeLineSubtotal, micrositeWeightLabel, type MicrositeLocale } from '@/lib/microsite/couponUx'
import {
  isRetailWeightItem,
  onlineWeightUnitOf,
  selectableVariants,
  stepWeightMilli,
  type RetailPickerFacts,
} from '@/lib/microsite/retailLine'
import { ONLINE_WEIGHT_RULES } from '@/lib/retail/onlineWeight'

type Props = {
  item: RetailPickerFacts & { price: number }
  pricingCfg: PricingConfig
  accentColor: string
  /** Idioma de la tienda: separador decimal del peso (R5). */
  locale: MicrositeLocale
  weightMilli: number
  onWeightChange: (weightMilli: number) => void
  variantId: string | null
  onVariantChange: (variantId: string) => void
  /** Variantes agotadas (stock en vivo, Tarea 2b.3). Ausente = ninguna. */
  soldOutVariantIds?: ReadonlySet<string>
  /** «Quedan N» por variante (stock en vivo, Tarea 2b.3). Ausente = no se muestra. */
  stockLeftByVariant?: Readonly<Record<string, number>>
  /** Producto por peso: «Quedan 2,5 lb» (stock en vivo, Tarea 2b.3), en su unidad. Ausente = no se muestra. */
  weightStockLeft?: number
}

const money = (n: number) => `$${n.toFixed(2)}`

export function RetailLinePicker({
  item,
  pricingCfg,
  accentColor,
  locale,
  weightMilli,
  onWeightChange,
  variantId,
  onVariantChange,
  soldOutVariantIds,
  stockLeftByVariant,
  weightStockLeft,
}: Props) {
  const t = useTranslations('public.microsite')

  if (isRetailWeightItem(item)) {
    const unit = onlineWeightUnitOf(item.weight_unit)
    // Unidad mal configurada: no hay peso que pedir (el modal deja «Agregar» apagado).
    if (!unit) return null
    const { stepMilli, maxMilli } = ONLINE_WEIGHT_RULES[unit]
    const weight = micrositeWeightLabel(weightMilli, unit, locale)
    // Por libra/kilo SIN el redondeo de $0,50, como lo cobra el servidor.
    const perUnit = effectiveUnitPrice(item.price, pricingCfg)
    const total = micrositeLineSubtotal(
      { cartId: '', itemId: '', name: '', price: item.price, quantity: 1, modifiers: [], weightMilli, weightUnit: unit },
      pricingCfg,
    )
    return (
      <div className="mt-5" data-testid="retail-weight-picker">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">{t('weightPerUnit', { price: money(perUnit), unit, weight, total: money(total) })}</span>
          <div className="flex items-center gap-3">
            <button
              type="button"
              aria-label={t('weightLess')}
              disabled={weightMilli <= stepMilli}
              onClick={() => onWeightChange(stepWeightMilli(weightMilli, -1, unit))}
              className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 disabled:opacity-50"
            >
              −
            </button>
            {/* Minor 3: el lector de pantalla anuncia el peso nuevo tras cada toque. */}
            <span aria-live="polite" aria-atomic="true" className="min-w-[4.5rem] text-center font-semibold">{weight}</span>
            <button
              type="button"
              aria-label={t('weightMore')}
              disabled={weightMilli >= maxMilli}
              onClick={() => onWeightChange(stepWeightMilli(weightMilli, 1, unit))}
              className="w-9 h-9 rounded-full bg-gray-100 text-gray-700 font-bold hover:bg-gray-200 disabled:opacity-50"
            >
              +
            </button>
          </div>
        </div>
        {typeof weightStockLeft === 'number' && weightStockLeft > 0 ? (
          <p className="mt-2 text-xs font-semibold text-gray-500">
            {t('onlyLeftWeight', { amount: weightStockLeft, weight: micrositeWeightLabel(weightStockLeft * 1000, unit, locale) })}
          </p>
        ) : null}
        <p className="mt-2 text-xs text-gray-500">{t('weightEstimateNote')}</p>
      </div>
    )
  }

  const variants = selectableVariants(item.variants, soldOutVariantIds)
  return (
    <fieldset className="mt-5" data-testid="retail-variant-picker">
      <legend className="font-semibold text-gray-900">{t('chooseVariant')}</legend>
      <div className="mt-2 space-y-2">
        {variants.map((v) => {
          const selected = v.id === variantId
          const soldOut = soldOutVariantIds?.has(v.id) === true
          const left = stockLeftByVariant?.[v.id]
          return (
            <label
              key={v.id}
              className={`w-full flex items-center justify-between gap-3 px-3 py-2.5 rounded-xl border transition ${
                v.disabled
                  ? 'border-gray-200 bg-gray-50 cursor-not-allowed'
                  : selected
                    ? 'border-gray-900 bg-gray-50 cursor-pointer'
                    : 'border-gray-200 hover:border-gray-300 cursor-pointer'
              }`}
            >
              <span className="flex items-center gap-3">
                <input
                  type="radio"
                  name="retail-variant"
                  value={v.id}
                  checked={selected}
                  disabled={v.disabled}
                  onChange={() => onVariantChange(v.id)}
                  className="w-4 h-4"
                  style={{ accentColor }}
                />
                <span className={`text-sm ${v.disabled ? 'text-gray-400 line-through' : 'text-gray-900'}`}>{v.name}</span>
                {soldOut ? (
                  <span className="text-[11px] font-bold uppercase tracking-wide text-gray-500">{t('soldOut')}</span>
                ) : !v.disabled && typeof left === 'number' && left > 0 ? (
                  <span className="text-[11px] font-semibold text-gray-500">{t('onlyLeft', { count: left })}</span>
                ) : null}
              </span>
              <span className="text-sm text-gray-600">{money(effectiveItemCardPrice(v.price ?? item.price, pricingCfg))}</span>
            </label>
          )
        })}
      </div>
    </fieldset>
  )
}
